/**
 * Shared utilities for item list components (TaskList, ExpenseList, NoteGrid, DateList, ChoreWheelList)
 */

import { isRecurringItem } from '@perfin/shared';
import type { Item } from '@perfin/shared';
import type { TabKey } from '@/stores/uiStore';

// ============================================================================
// DATE PARSING
// ============================================================================

/**
 * Parse a date value that could be:
 * - A plain JavaScript Date
 * - A Firestore Timestamp (has toDate() method)
 * - A serialized date object with seconds
 * - A string/number timestamp
 */
export function parseDate(d: unknown): Date | null {
  if (!d) return null;
  if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
    return (d as { toDate: () => Date }).toDate();
  }
  if (d instanceof Date) return d;
  if (typeof d === 'object' && d !== null && 'seconds' in d) {
    return new Date((d as { seconds: number }).seconds * 1000);
  }
  return new Date(d as string | number);
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Format date for grouping display (e.g., "Mar 7, 2025")
 */
export function formatDateKey(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ============================================================================
// DATE RANGE UTILITIES
// ============================================================================

/**
 * Get date range for time-based tabs (pastWeek, today, nextWeek)
 * Returns null for non-time-based tabs (all, allScheduled, allUnscheduled, allRecurring)
 */
export function getDateRangeForTab(tab: TabKey, referenceDate: Date): { start: Date; end: Date } | null {
  const refDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());

  switch (tab) {
    case 'pastWeek': {
      const start = new Date(refDate);
      start.setDate(refDate.getDate() - 7);
      return { start, end: new Date(refDate.getTime() - 1) }; // Up to but not including refDate
    }
    case 'today': {
      const end = new Date(refDate);
      end.setDate(refDate.getDate() + 1);
      return { start: refDate, end: new Date(end.getTime() - 1) };
    }
    case 'nextWeek': {
      const start = new Date(refDate);
      start.setDate(refDate.getDate() + 1);
      const end = new Date(refDate);
      end.setDate(refDate.getDate() + 7);
      return { start, end };
    }
    default:
      return null;
  }
}

/**
 * Check if date falls within range (inclusive on both ends, normalized to midnight)
 */
export function isDateInRange(date: Date, start: Date, end: Date): boolean {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= s && d <= e;
}

// ============================================================================
// DISPLAY ENTRY TYPE
// ============================================================================

/**
 * Represents an item (or occurrence of a recurring item) to display in a list
 */
export interface DisplayEntry<T> {
  item: T;
  occurrenceDate?: Date;  // For recurring items: the specific occurrence date
  occurrenceStatus?: string;  // For recurring items: the status of this specific occurrence
  key: string;  // Unique key for React
}

// ============================================================================
// OCCURRENCE EXPANSION
// ============================================================================

/**
 * Expand items to display entries (one per occurrence for recurring items in time-based tabs)
 *
 * @param items - The items to expand
 * @param currentTab - Current tab (determines if expansion is needed)
 * @param referenceDate - Reference date for time-based tabs
 * @param statusFilter - Optional status filter for occurrence-level filtering
 * @returns Array of display entries (one per item/occurrence)
 */
export function expandItemsToEntries<T extends Item>(
  items: T[],
  currentTab: TabKey | undefined,
  referenceDate: Date,
  statusFilter?: string | null
): DisplayEntry<T>[] {
  const isTimeBased = currentTab === 'pastWeek' || currentTab === 'today' || currentTab === 'nextWeek';
  const refDate = referenceDate || new Date();

  // For non-time-based tabs (allScheduled, allUnscheduled, allRecurring, all), no expansion
  if (!isTimeBased || !currentTab) {
    return items.map(item => ({
      item,
      occurrenceDate: undefined,
      key: item.id,
    }));
  }

  // For time-based tabs, expand recurring items to one entry per occurrence
  const dateRange = getDateRangeForTab(currentTab, refDate);
  if (!dateRange) {
    return items.map(item => ({
      item,
      occurrenceDate: undefined,
      key: item.id,
    }));
  }

  const entries: DisplayEntry<T>[] = [];

  for (const item of items) {
    if (isRecurringItem(item)) {
      // Recurring: create one entry per occurrence in the date range
      const occurrences = (item as unknown as { occurrences?: Array<{ date: unknown; status?: string }> }).occurrences || [];
      for (const occ of occurrences) {
        const occDate = parseDate(occ.date);
        if (occDate && isDateInRange(occDate, dateRange.start, dateRange.end)) {
          // Filter by occurrence status if statusFilter is set
          if (statusFilter && occ.status !== statusFilter) {
            continue;
          }
          entries.push({
            item,
            occurrenceDate: occDate,
            occurrenceStatus: occ.status,
            key: `${item.id}-${occDate.getTime()}`,
          });
        }
      }
    } else {
      // Non-recurring: single entry (no occurrenceDate)
      entries.push({
        item,
        occurrenceDate: undefined,
        key: item.id,
      });
    }
  }

  return entries;
}
